import type { CampaignSimpleResponse, PopupResponse } from '@coorum-research/shared-common/api-response/popup.schema'
import type { ThemeConfig } from '@coorum-research/shared-common/types/theme.type'
import { PageContainer } from '@coorum-research/core/src/components/PageContainer'
import { useOnceCall } from '@coorum-research/core/src/composables/use-once-call'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { useEffect, useMemo, useRef, useState } from 'react'
import { recordUserActionHistory } from '../utils/user-action-history.util'
import { CampaignSelectionApp } from './CampaignSelectionComponentApp'
import { featureFlags } from './feature-flag/feature-flag.config'
import { MyPage } from './MyPage'
import { SurveyAppAnswerCreate } from './SurveyAppAnswerCreate'
import { ThanksPage } from './ThanksPage'

export type Props = {
  onClose: () => void
  onShow?: () => void
  campaignSelectionContentId?: string | null
  popup?: PopupResponse
  popupTheme?: ThemeConfig
  campaigns?: CampaignSimpleResponse[]
  initialMode: PageMode
  initialCampaignId: string | null
}

export type PageMode = 'CampaignSelection' | 'survey' | 'thanks' | 'mypage'

export function CoorumResearch(props: Props) {
  const {
    onClose,
    onShow,
    campaignSelectionContentId,
    campaigns = [],
    popup,
    popupTheme,
    initialMode,
    initialCampaignId,
  } = props

  const [close, setClose] = useState(false)
  const [mode, setMode] = useState<PageMode | null>(initialMode)
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(initialCampaignId)
  const [launcherContentId, setLauncherContentId] = useState<string | null>(null)

  const containerRef = useRef<HTMLDivElement | null>(null)

  const campaign = useMemo(() => {
    return campaigns.find(({ id }) => id === selectedCampaignId)
  }, [initialMode, initialCampaignId, selectedCampaignId])

  const handleCloseState = () => {
    setMode(null)
    onClose()
  }

  // データ計測 (popupClick)
  /**
   * fixme
   * 1. 以下のあたりに実装して、useOnceCall使わずにevent発火時にrecordUserActionHistoryを呼びたい
   * https://github.com/asobica/coorum-research/blob/main/packages/shared-client-user/utils/RenderResearchPage.tsx#L53
   *
   * 2. useOnceCallの中にuseEffectがあり、それが条件付きで呼ばれることによるESLint errorを解消する
   */
  const isUserActionHistoriesEnabled = useBooleanFlagValue(featureFlags.ACCOUNT_USER_ACTION_HISTORIES, false)

  useOnceCall(() => {
    if (isUserActionHistoriesEnabled) {
      recordUserActionHistory({
        actionType: 'popupClick',
        popupId: popup?.id,
        popupName: popup?.name,
        campaignId: campaign?.id,
        campaignName: campaign?.name,
        // }).then(() => {
        //   console.log('recordUserActionHistory popupClick success')
      }).catch((error) => {
        console.error('recordUserActionHistory popupClick error', error)
      })
    }
  })

  const handleCloseButton = () => {
    switch (mode) {
    case 'survey':
      setClose(true)
      break
    case 'CampaignSelection':
      handleCloseState()
      break
    case 'thanks':
      handleCloseState()
      break
    case 'mypage':
      handleCloseState()
      break
    case null:
      break
    }
  }

  const handleCampaignSelect = (campaignId: string, campaignName: string) => {
    setSelectedCampaignId(campaignId)
    setMode('survey')

    // データ計測 (campaignClick)
    if (isUserActionHistoriesEnabled) {
      recordUserActionHistory({
        actionType: 'campaignClick',
        popupId: popup?.id,
        popupName: popup?.name,
        campaignId,
        campaignName,
        // }).then(() => {
        //   console.log('recordUserActionHistory campaignClick success')
      }).catch((error) => {
        console.error('recordUserActionHistory campaignClick error', error)
      })
    }
  }

  useEffect(() => {
    if (onShow) {
      onShow()
    }
  }, [])

  useEffect(() => {
    setMode(initialMode)
    setSelectedCampaignId(initialCampaignId)
  }, [initialMode, initialCampaignId])

  return (
    <PageContainer
      className={mode === 'mypage' ? 'bg-[#F6F6F6]' : 'bg-white'}
      ref={containerRef}
      onClickClose={handleCloseButton}
    >
      {mode === 'CampaignSelection'
        ? (
          <CampaignSelectionApp
            campaignSelectionContentId={campaignSelectionContentId}
            theme={popupTheme}
            campaigns={campaigns ?? []}
            onSelected={handleCampaignSelect}
          />
        )
        : mode === 'survey' && campaign
          ? (
            <SurveyAppAnswerCreate
              popup={popup}
              campaign={campaign}
              surveyId={campaign.surveyId ?? ''}
              onComplete={(nextLaunchContentId: string | null) => {
                setLauncherContentId(nextLaunchContentId)
                setMode('thanks')
              }}
              containerRef={containerRef}
              isInterrupt={close}
              setIsInterrupt={setClose}
              onClose={() => handleCloseState()}
            />
          )
          : mode === 'thanks'
            ? (
              <ThanksPage
                openedFrom="create"
                popup={popup}
                campaign={campaign}
                launcherContentId={launcherContentId}
                onClickButton={() => setMode('mypage')}
              />
            )
            : mode === 'mypage'
              ? (
                <MyPage
                  containerRef={containerRef}
                />
              )
              : <p className="text-center">表示するコンテンツがありません</p>}
    </PageContainer>
  )
}
